import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Avatar,
  Button,
  Collapse,
  Empty,
  Input,
  Modal,
  Pagination,
  Radio,
  Spin,
  Tabs,
  TreeSelect,
  Typography,
  message,
} from 'antd'
import { ReloadOutlined, UserOutlined } from '@ant-design/icons'
import { adminApi, formatApiError, PAGE_SIZE, type PageResult } from '../../api'
import { BROWSE_PICKER_Z_INDEX } from './ObjectBrowseField'
import { tablePagination } from './tablePagination'
import './PersonPickerModal.css'

export type PersonBrowseItem = {
  id: number
  user_id: number | null
  selectable: boolean
  account_status: string
  has_avatar: boolean
  updated_at?: string | null
  name: string
  employee_no: string
  position: string
  org_unit_name: string
  branch_name: string
  org_path: string
  subtitle: string
  email: string
  mobile: string
}

export type PersonPickValue = {
  userId: number
  personId: number
  name: string
}

type OrgTreeNode = {
  id: number
  name: string
  children?: OrgTreeNode[]
}

type Props = {
  open: boolean
  value?: PersonPickValue | null
  onCancel: () => void
  onConfirm: (value: PersonPickValue | null) => void
}

const SOURCE_TABS = [
  { key: 'recent', label: '最近', enabled: false },
  { key: 'org', label: '组织架构', enabled: true },
  { key: 'same_dept', label: '同部门', enabled: false },
  { key: 'subordinates', label: '我的下属', enabled: false },
  { key: 'all', label: '所有人', enabled: true },
  { key: 'group', label: '群组', enabled: false },
  { key: 'following', label: '我的关注', enabled: false },
]

function personAvatarUrl(personId: number, updatedAt?: string | null) {
  const ts = updatedAt ? `?t=${encodeURIComponent(updatedAt)}` : ''
  return `/api/admin/org-units/persons/${personId}/avatar${ts}`
}

function PersonAvatar({ item, size = 28 }: { item: PersonBrowseItem; size?: number }) {
  const [failed, setFailed] = useState(false)
  const showImg = item.has_avatar && !failed
  return (
    <Avatar
      size={size}
      src={showImg ? personAvatarUrl(item.id, item.updated_at) : undefined}
      onError={() => {
        setFailed(true)
        return false
      }}
      className="person-picker-avatar"
    >
      {item.name?.slice(0, 1) || '?'}
    </Avatar>
  )
}

function toTreeSelectData(nodes: OrgTreeNode[]): { title: string; value: number; children?: ReturnType<typeof toTreeSelectData> }[] {
  return nodes.map((n) => ({
    title: n.name,
    value: n.id,
    children: n.children?.length ? toTreeSelectData(n.children) : undefined,
  }))
}

export function PersonPickerModal({ open, value, onCancel, onConfirm }: Props) {
  const [sourceTab, setSourceTab] = useState('all')
  const [keyword, setKeyword] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [orgUnitId, setOrgUnitId] = useState<number | undefined>()
  const [orgTree, setOrgTree] = useState<OrgTreeNode[]>([])
  const [items, setItems] = useState<PersonBrowseItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<PersonBrowseItem | null>(null)
  const [selectedKeyword, setSelectedKeyword] = useState('')

  const activeTab = SOURCE_TABS.find((t) => t.key === sourceTab)
  const tabEnabled = !!activeTab?.enabled

  const loadTree = useCallback(() => {
    adminApi
      .get<{ items: OrgTreeNode[] }>('/admin/org-units/tree', { params: { active_only: true } })
      .then((r) => setOrgTree(r.data.items || []))
      .catch(() => {})
  }, [])

  const loadPersons = useCallback(() => {
    if (!tabEnabled) {
      setItems([])
      setTotal(0)
      return
    }
    setLoading(true)
    adminApi
      .get<PageResult<PersonBrowseItem>>('/admin/org-units/persons/browse', {
        params: {
          page,
          page_size: PAGE_SIZE,
          keyword: keyword || undefined,
          org_unit_id: sourceTab === 'org' ? orgUnitId : undefined,
        },
      })
      .then((r) => {
        setItems(r.data.items || [])
        setTotal(r.data.total || 0)
      })
      .catch((e) => message.error(formatApiError(e, '加载人员失败')))
      .finally(() => setLoading(false))
  }, [tabEnabled, page, keyword, orgUnitId, sourceTab])

  useEffect(() => {
    if (!open) return
    setSourceTab('all')
    setKeyword('')
    setSearchInput('')
    setOrgUnitId(undefined)
    setPage(1)
    setSelectedKeyword('')
    loadTree()
    if (value) {
      setSelected({
        id: value.personId,
        user_id: value.userId,
        selectable: true,
        account_status: 'active',
        has_avatar: false,
        name: value.name,
        employee_no: '',
        position: '',
        org_unit_name: '',
        branch_name: '',
        org_path: '',
        subtitle: '',
        email: '',
        mobile: '',
      })
    } else {
      setSelected(null)
    }
  }, [open, value, loadTree])

  useEffect(() => {
    if (!open) return
    loadPersons()
  }, [open, loadPersons])

  const treeSelectData = useMemo(() => toTreeSelectData(orgTree), [orgTree])

  const filteredSelected = useMemo(() => {
    if (!selected) return null
    const kw = selectedKeyword.trim().toLowerCase()
    if (!kw) return selected
    const hay = `${selected.name} ${selected.subtitle} ${selected.org_path}`.toLowerCase()
    return hay.includes(kw) ? selected : null
  }, [selected, selectedKeyword])

  const pickPerson = (item: PersonBrowseItem) => {
    if (!item.selectable || !item.user_id) {
      message.warning('该人员尚未分配系统账号，无法选择')
      return
    }
    setSelected(item)
  }

  const handleConfirm = () => {
    if (!selected?.user_id) {
      message.warning('请选择人员')
      return
    }
    onConfirm({
      userId: selected.user_id,
      personId: selected.id,
      name: selected.name,
    })
  }

  return (
    <Modal
      className="person-picker-modal"
      title={
        <span className="person-picker-title">
          <UserOutlined />
          选择人员
        </span>
      }
      open={open}
      onCancel={onCancel}
      cancelText="取消"
      footer={null}
      width={820}
      zIndex={BROWSE_PICKER_Z_INDEX}
      destroyOnClose
      centered
    >
      <div className="person-picker-layout">
        <div className="person-picker-left">
          <Tabs
            className="person-picker-source-tabs"
            activeKey={sourceTab}
            onChange={(k) => {
              setSourceTab(k)
              setPage(1)
            }}
            items={SOURCE_TABS.map((t) => ({ key: t.key, label: t.label, disabled: !t.enabled }))}
          />

          <div className="person-picker-toolbar">
            <Input.Search
              allowClear
              size="small"
              placeholder="请输入姓名/首字母/全拼/别名/工号/邮箱/手机/电话"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onSearch={(v) => {
                setKeyword(v.trim())
                setPage(1)
              }}
            />
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              aria-label="刷新"
              onClick={() => loadPersons()}
            />
          </div>

          <div className="person-picker-org-filter-slot">
            {sourceTab === 'org' ? (
              <TreeSelect
                allowClear
                showSearch
                size="small"
                treeDefaultExpandAll
                placeholder="选择组织节点筛选"
                style={{ width: '100%' }}
                value={orgUnitId}
                treeData={treeSelectData}
                treeNodeFilterProp="title"
                onChange={(v) => {
                  setOrgUnitId(typeof v === 'number' ? v : undefined)
                  setPage(1)
                }}
              />
            ) : (
              <span className="person-picker-org-filter-placeholder" aria-hidden />
            )}
          </div>

          <div className="person-picker-left-body">
            {!tabEnabled ? (
              <div className="person-picker-unavailable">该分类暂未开放</div>
            ) : (
              <>
                <Collapse
                  ghost
                  className="person-picker-group"
                  defaultActiveKey={['admin-org']}
                  items={[
                    {
                      key: 'admin-org',
                      label: '行政组织',
                      children: (
                        <Spin spinning={loading}>
                          <div className="person-picker-list">
                            {items.length ? (
                              items.map((item) => (
                                <div
                                  key={item.id}
                                  className={`person-picker-row${selected?.id === item.id ? ' is-selected' : ''}${!item.selectable ? ' is-disabled' : ''}`}
                                  onClick={() => pickPerson(item)}
                                >
                                  <PersonAvatar item={item} />
                                  <div className="person-picker-row-main">
                                    <div className="person-picker-row-name">{item.name}</div>
                                    <div className="person-picker-row-sub">
                                      {item.subtitle || '—'}
                                      {!item.selectable ? (
                                        <Typography.Text type="secondary">（未分配账号）</Typography.Text>
                                      ) : null}
                                    </div>
                                    <div className="person-picker-row-path">{item.org_path || '—'}</div>
                                  </div>
                                  <Radio checked={selected?.id === item.id} disabled={!item.selectable} />
                                </div>
                              ))
                            ) : (
                              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无人员" />
                            )}
                          </div>
                        </Spin>
                      ),
                    },
                  ]}
                />

                <div className="person-picker-pagination">
                  <Pagination
                    {...tablePagination(page, total, (p) => setPage(p), { pageSize: PAGE_SIZE })}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="person-picker-right">
          <Input
            allowClear
            size="small"
            placeholder="请输入关键字"
            value={selectedKeyword}
            onChange={(e) => setSelectedKeyword(e.target.value)}
            className="person-picker-selected-search"
          />
          <div className={`person-picker-selected-box${filteredSelected ? '' : ' is-empty'}`}>
            {filteredSelected ? (
              <div className="person-picker-selected-item">
                <PersonAvatar item={filteredSelected} size={24} />
                <div className="person-picker-row-main">
                  <div className="person-picker-row-name">{filteredSelected.name}</div>
                  <div className="person-picker-row-sub">{filteredSelected.subtitle || '—'}</div>
                  <div className="person-picker-row-path">{filteredSelected.org_path || '—'}</div>
                </div>
                <Button type="link" size="small" onClick={() => setSelected(null)}>
                  移除
                </Button>
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div className="person-picker-empty-desc">
                    <div>暂无已选数据</div>
                    <Typography.Text type="secondary">请从左侧选择需要添加的内容</Typography.Text>
                  </div>
                }
              />
            )}
          </div>
          <div className="person-picker-right-actions">
            <Button type="primary" size="small" onClick={handleConfirm}>
              确定({selected ? 1 : 0})
            </Button>
            <Button size="small" onClick={onCancel}>
              取消
            </Button>
            <Button size="small" onClick={() => setSelected(null)} disabled={!selected}>
              清空已选
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
